import { useMemo } from 'react';
import { Outlet, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  DashboardShell,
  NotificationStrip,
  createShellNavLink,
  type ShellNavItem,
} from '@vipso/shared';
import {
  IconHome,
  IconChecklist,
  IconPackage,
  IconShoppingCart,
  IconUsers,
  IconBuildingStore,
  IconSettings,
  IconUserCheck,
  IconBuildingWarehouse,
  IconTruck,
  IconHammer,
  IconApps,
  IconMapPin,
  IconArrowsTransferDown,
  IconShieldHalfFilled,
  IconHierarchy2,
} from '@tabler/icons-react';
import { logout } from '../store/authSlice';
import type { RootState } from '../store';
import { resolveDisplayLogoUrl } from '../utils/resolveDisplayLogoUrl';
import { useSystemNotificationsQuery } from '../api/enhanceGenerated';
import { useModuleLabel } from '../hooks/useModuleLabel';
import { useNavAccess } from '../hooks/useNavAccess';

const ShellNavLink = createShellNavLink(NavLink);

export function AppShellLayout() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const nav = useNavAccess();

  const customersLabel = useModuleLabel('customer');
  const vendorsLabel = useModuleLabel('vendor');
  const productsLabel = useModuleLabel('product');

  const navItems: ShellNavItem[] = useMemo(() => {
    const items: ShellNavItem[] = [
      { to: '/dashboard', label: t('nav.home'), icon: IconHome, end: true },
    ];
    if (nav.customers) {
      items.push({ to: '/customers', label: customersLabel, icon: IconUsers });
    }
    if (nav.vendors) {
      items.push({ to: '/vendors', label: vendorsLabel, icon: IconBuildingStore });
    }
    if (nav.products) {
      items.push({ to: '/products', label: productsLabel, icon: IconPackage });
    }
    if (nav.orders) {
      items.push({ to: '/orders', label: t('nav.orders'), icon: IconShoppingCart });
    }
    if (nav.staff) {
      items.push({ to: '/staff', label: t('nav.staff'), icon: IconUserCheck });
    }
    if (nav.roles) {
      items.push({ to: '/roles', label: t('nav.roles'), icon: IconShieldHalfFilled });
    }
    if (nav.approvalFlows) {
      items.push({
        to: '/approval-flows',
        label: t('nav.approvalFlows'),
        icon: IconHierarchy2,
      });
    }
    if (nav.warehouse) {
      items.push({
        to: '/warehouse',
        label: t('nav.warehouse'),
        icon: IconBuildingWarehouse,
      });
    }
    if (nav.storage) {
      items.push({ to: '/storage', label: t('nav.storage'), icon: IconMapPin });
    }
    if (nav.inventory) {
      items.push({
        to: '/inventory',
        label: t('nav.inventory'),
        icon: IconArrowsTransferDown,
      });
    }
    if (nav.transport) {
      items.push({ to: '/transport', label: t('nav.transport'), icon: IconTruck });
    }
    if (nav.labour) {
      items.push({ to: '/labour', label: t('nav.labour'), icon: IconHammer });
    }
    if (nav.tasks) {
      items.push({ to: '/tasks', label: t('nav.tasks'), icon: IconChecklist });
    }
    if (nav.settings) {
      items.push({ to: '/settings', label: t('nav.settings'), icon: IconSettings });
    }
    return items;
  }, [t, customersLabel, vendorsLabel, productsLabel, nav]);

  const moreActivePatterns = useMemo(() => {
    const patterns: string[] = [];
    if (nav.customers) patterns.push('/customers');
    if (nav.vendors) patterns.push('/vendors');
    if (nav.staff) patterns.push('/staff');
    if (nav.roles) patterns.push('/roles');
    if (nav.approvalFlows) patterns.push('/approval-flows');
    if (nav.warehouse) patterns.push('/warehouse');
    if (nav.storage) patterns.push('/storage');
    if (nav.inventory) patterns.push('/inventory');
    if (nav.transport) patterns.push('/transport');
    if (nav.labour) patterns.push('/labour');
    if (nav.settings) patterns.push('/settings');
    return patterns;
  }, [nav]);

  const mobileNavItems: ShellNavItem[] = useMemo(() => {
    const items: ShellNavItem[] = [
      { to: '/dashboard', label: t('nav.home'), icon: IconHome, end: true },
    ];
    if (nav.tasks) {
      items.push({ to: '/tasks', label: t('nav.tasks'), icon: IconChecklist });
    }
    if (nav.products) {
      items.push({ to: '/products', label: productsLabel, icon: IconPackage });
    }
    if (nav.orders) {
      items.push({ to: '/orders', label: t('nav.orders'), icon: IconShoppingCart });
    }
    items.push({
      to: '/more',
      label: t('nav.more'),
      icon: IconApps,
      activePatterns: moreActivePatterns,
    });
    return items;
  }, [t, productsLabel, nav, moreActivePatterns]);

  const mobileDrawerItems: ShellNavItem[] = useMemo(() => {
    const items: ShellNavItem[] = [
      { to: '/dashboard', label: t('nav.home'), icon: IconHome, end: true },
    ];
    if (nav.tasks) {
      items.push({ to: '/tasks', label: t('nav.tasks'), icon: IconChecklist });
    }
    if (nav.products) {
      items.push({ to: '/products', label: productsLabel, icon: IconPackage });
    }
    if (nav.orders) {
      items.push({ to: '/orders', label: t('nav.orders'), icon: IconShoppingCart });
    }
    items.push({ to: '/more', label: t('nav.more'), icon: IconApps });
    if (nav.settings) {
      items.push({ to: '/settings', label: t('nav.settings'), icon: IconSettings });
    }
    return items;
  }, [t, productsLabel, nav]);

  const { user, org, orgs, theme, config, tenant, pendingTaskCount } = useSelector(
    (state: RootState) => state.auth,
  );

  // Socket patches this cache in place; avoid aggressive polling that fights live updates.
  const { data: notifData } = useSystemNotificationsQuery(undefined, {
    pollingInterval: 5 * 60_000,
    refetchOnFocus: true,
    refetchOnReconnect: true,
  });
  const notifications = notifData?.systemNotifications ?? [];

  const tasksLabel =
    pendingTaskCount > 0
      ? t('nav.tasksWithCount', { count: pendingTaskCount })
      : t('nav.tasks');
  const displayLogoUrl = resolveDisplayLogoUrl(org, config, theme, {
    tenantSlug: tenant?.slug,
  });

  const desktopNav = navItems.map((item) =>
    item.to === '/tasks' ? { ...item, label: tasksLabel } : item,
  );
  const mobileNav = mobileNavItems.map((item) =>
    item.to === '/tasks' ? { ...item, label: tasksLabel } : item,
  );
  const drawerNav = mobileDrawerItems.map((item) =>
    item.to === '/tasks' ? { ...item, label: tasksLabel } : item,
  );

  function handleLogout() {
    dispatch(logout());
    navigate('/login', { replace: true });
  }

  const canSwitchOrg = orgs.length > 1;
  const handleSwitchOrg = () => navigate('/org-picker');
  const profileLinks = [
    {
      label: t('nav.settings'),
      onClick: () => navigate('/settings'),
    },
    ...(canSwitchOrg
      ? [
          {
            label: t('orgPicker.switchOrganization'),
            onClick: handleSwitchOrg,
          },
        ]
      : []),
  ];

  return (
    <DashboardShell
      LinkComponent={ShellNavLink}
      navItems={desktopNav}
      mobileNavItems={mobileNav}
      mobileDrawerItems={drawerNav}
      currentPath={location.pathname}
      brandTitle={org?.name || 'VIPSo'}
      logoUrl={displayLogoUrl}
      orgName={org?.name}
      user={user}
      notificationBanner={
        notifications.length > 0 ? (
          <NotificationStrip notifications={notifications} />
        ) : undefined
      }
      profileLinks={profileLinks}
      onOrgSwitch={canSwitchOrg ? handleSwitchOrg : undefined}
      orgSwitchLabel={t('orgPicker.switchOrganization')}
      onLogout={handleLogout}
    >
      <Outlet />
    </DashboardShell>
  );
}
